import { useEffect, useMemo, useRef, useState } from 'react'
import { Check, Copy, Info, Wallet } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { DataTableToolbar, type FilterDef, type TableState } from '@/components/blocks/data-table'
import {
  Card,
  CardDescription,
  CardHeader,
  CardList,
  CardListRow,
  CardTitle,
} from '@/components/ui/Card'
import { useToast } from '@/components/ui/Toast'
import { cn } from '@/lib/utils'

import { AvatarCell, StatCard } from '@/components/shared'
import { count, minutesAgo, money } from '@/lib'
import type {
  BanRecord,
  Battle,
  GoldHistoryEntry,
  InGameMail,
  Player,
  PlayerCard,
  PlayerTransaction,
  Rarity,
  Stat,
} from '@/models'

/* One header, five panels. Every log on this page is the same Card + CardList,
 * so they share a rhythm instead of each one picking its own padding. */
function SectionCard({
  title,
  description,
  children,
}: {
  title: string
  description: string
  children: React.ReactNode
}) {
  return (
    <Card>
      <CardHeader>
        <div className="min-w-0">
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
      </CardHeader>
      <CardList>{children}</CardList>
    </Card>
  )
}

export function PlayerTopCards({ player }: { player: Player }) {
  /* Rank and SVIP are labelled figures, so they are the same <StatCard> the
     other pages use rather than a second house style for the same idea. They
     carry a `meta` line, not a delta — neither is a comparison. */
  const stats: Stat[] = [
    {
      id: 'rank',
      label: 'Rank',
      value: player.rank <= 3 ? `Top #${player.rank}` : 'Unranked',
      meta: `Raw elo: ${count(player.rank <= 3 ? player.trophies : 0)}`,
    },
    {
      id: 'svip',
      label: 'SVIP',
      value: `Level ${player.svipLevel}`,
      meta: 'Expires: 0',
    },
  ]

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <Card className="flex h-full flex-col justify-center px-5 py-4">
        <div className="flex items-center gap-4">
          <AvatarCell name={player.username} className="size-12 shrink-0 text-lg" />
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2">
              <h2 className="text-fg m-0 truncate text-base leading-none font-bold">
                {player.username}
              </h2>
              <span className="text-fg-subtle shrink-0 text-xs leading-none font-medium">
                {player.id.startsWith('#') ? player.id : `#${player.id}`}
              </span>
            </div>
            <p className="text-fg mt-1.5 mb-0 text-sm leading-tight font-semibold">
              Level {player.level}{' '}
              <span className="text-fg-subtle font-normal">· {count(player.exp ?? 200)} XP</span>
            </p>
            <p className="text-fg-subtle mt-1 mb-0 truncate text-sm leading-tight">
              {player.bio ?? 'No bio yet.'}
            </p>
          </div>
        </div>
      </Card>

      {stats.map((stat) => (
        <StatCard key={stat.id} stat={stat} />
      ))}
    </div>
  )
}

/** Label on the left, value on the right — the shape of every row in the two
 *  sidebar cards, so they line up with each other. */
function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-0.5 text-sm">
      <span className="text-fg-subtle shrink-0 font-medium">{label}</span>
      {children}
    </div>
  )
}

export function PlayerSidebarCards({ player }: { player: Player }) {
  const { toast } = useToast()
  const [copiedKey, setCopiedKey] = useState<string | null>(null)
  const resetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => () => clearTimeout(resetTimer.current), [])

  const copyToClipboard = (text: string, label: string, key: string) => {
    void navigator.clipboard.writeText(text)
    setCopiedKey(key)
    toast(`Copied ${label} to clipboard`, 'success')
    clearTimeout(resetTimer.current)
    resetTimer.current = setTimeout(() => setCopiedKey(null), 2000)
  }

  const copyButton = (value: string, label: string, key: string, truncate = false) => (
    <button
      type="button"
      onClick={() => copyToClipboard(value, label, key)}
      className={cn(
        'group text-fg flex min-w-0 items-center gap-1.5 rounded-sm font-medium outline-none',
        'hover:text-accent transition-colors duration-100 ease-out',
        'focus-visible:outline-accent focus-visible:outline-2 focus-visible:outline-offset-2',
        truncate && 'max-w-38',
      )}
    >
      <span className={cn(truncate && 'truncate')} translate="no">
        {value}
      </span>
      {copiedKey === key ? (
        <Check aria-hidden className="text-ok size-3.5 shrink-0" />
      ) : (
        <Copy
          aria-hidden
          className="text-fg-subtle size-3.5 shrink-0 opacity-70 transition-opacity duration-100 ease-out group-hover:opacity-100"
        />
      )}
      <span className="sr-only">Copy {label}</span>
    </button>
  )

  return (
    <div className="space-y-4">
      <Card className="px-5 py-4">
        <h3 className="text-fg m-0 mb-4 flex items-center gap-2 text-sm font-semibold">
          <Wallet aria-hidden className="text-fg-subtle size-4" />
          Wallets
        </h3>
        <div className="space-y-3">
          <InfoRow label="Gold">
            <span className="text-fg font-semibold tabular-nums">
              {count(player.gold)} <span aria-hidden>🪙</span>
            </span>
          </InfoRow>
          <InfoRow label="Cara">
            <span className="text-fg font-semibold tabular-nums">
              {count(player.cara)} <span aria-hidden>🩷</span>
            </span>
          </InfoRow>
        </div>
      </Card>

      <Card className="px-5 py-4">
        <h3 className="text-fg m-0 mb-4 flex items-center gap-2 text-sm font-semibold">
          <Info aria-hidden className="text-fg-subtle size-4" />
          Information
        </h3>
        <div className="space-y-3">
          <InfoRow label="Role">
            <span className="border-elixir/30 bg-elixir/10 text-elixir inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold capitalize">
              <span aria-hidden className="bg-elixir size-1.5 rounded-full" />
              {player.role.toLowerCase()}
            </span>
          </InfoRow>

          <InfoRow label="Referral code">
            {player.referralCode ? (
              copyButton(player.referralCode, 'referral code', 'code')
            ) : (
              <span className="text-fg-subtle font-medium">none</span>
            )}
          </InfoRow>

          <InfoRow label="Referral URL">
            {player.referralUrl ? (
              copyButton(player.referralUrl, 'referral URL', 'url', true)
            ) : (
              <span className="text-fg-subtle font-medium">none</span>
            )}
          </InfoRow>

          <InfoRow label="Last login">
            <span className="text-fg font-medium">{minutesAgo(player.lastSeenMin)}</span>
          </InfoRow>

          <InfoRow label="Created">
            <span className="text-fg font-medium">{player.joined}</span>
          </InfoRow>
        </div>
      </Card>
    </div>
  )
}

export function PlayerTransactionsList({
  transactions,
}: {
  transactions: readonly PlayerTransaction[]
}) {
  return (
    <SectionCard
      title="IAP Transactions Log"
      description="Real-time store purchases and payment records."
    >
      {transactions.map((tx) => (
        <CardListRow key={tx.id} className="gap-4">
          <span className="text-fg-subtle shrink-0 text-sm" translate="no">
            {tx.id}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-fg m-0 truncate text-sm font-semibold">{tx.packName}</p>
            <p className="text-fg-subtle m-0 truncate text-xs">Channel: {tx.channel}</p>
          </div>
          <Badge
            variant={
              tx.status === 'Completed' ? 'success' : tx.status === 'Pending' ? 'warning' : 'danger'
            }
          >
            {tx.status}
          </Badge>
          <span className="text-fg shrink-0 text-sm font-bold tabular-nums">
            {money(tx.amount)}
          </span>
          <span className="text-fg-subtle w-24 shrink-0 text-right text-xs tabular-nums">
            {tx.timestamp}
          </span>
        </CardListRow>
      ))}
    </SectionCard>
  )
}

export function PlayerGoldHistoryList({ entries }: { entries: readonly GoldHistoryEntry[] }) {
  return (
    <SectionCard
      title="Currency & Gold History"
      description="Audit log of Gold and Cara (Gems) transaction changes."
    >
      {entries.map((entry) => (
        <CardListRow key={entry.id} className="gap-4">
          <Badge variant="outline" className="shrink-0">
            {entry.currencyType === 'Gold' ? (
              <>
                Gold <span aria-hidden>🪙</span>
              </>
            ) : (
              <>
                Cara <span aria-hidden>🩷</span>
              </>
            )}
          </Badge>
          <div className="min-w-0 flex-1">
            <p className="text-fg m-0 truncate text-sm font-medium">{entry.action}</p>
            <p className="text-fg-subtle mt-0.5 mb-0 text-xs">
              Balance after:{' '}
              <span className="text-fg font-semibold tabular-nums">
                {count(entry.balanceAfter)}
              </span>
            </p>
          </div>
          <span
            className={cn(
              'shrink-0 text-sm font-semibold tabular-nums',
              entry.isPositive ? 'text-ok' : 'text-err',
            )}
          >
            {entry.change}
          </span>
          <span className="text-fg-subtle w-20 shrink-0 text-right text-xs tabular-nums">
            {entry.timestamp}
          </span>
        </CardListRow>
      ))}
    </SectionCard>
  )
}

export function PlayerMailsList({ mails }: { mails: readonly InGameMail[] }) {
  return (
    <SectionCard title="In-Game Mail Inbox" description="System notices and reward mail delivery.">
      {mails.map((mail) => (
        <CardListRow key={mail.id} className="gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-fg truncate text-sm font-semibold">{mail.title}</span>
              {!mail.read && <Badge variant="danger">Unread</Badge>}
            </div>
            {mail.rewards && (
              <p className="text-fg-muted mt-0.5 mb-0 truncate text-xs font-medium">
                Rewards: {mail.rewards}
              </p>
            )}
          </div>
          <span className="text-fg-subtle shrink-0 text-xs tabular-nums">{mail.sentAt}</span>
        </CardListRow>
      ))}
    </SectionCard>
  )
}

const CARD_FILTERS: FilterDef[] = [
  {
    key: 'rarity',
    label: 'All rarities',
    options: [
      { value: '', label: 'All rarities' },
      { value: 'Legendary', label: 'Legendary' },
      { value: 'Epic', label: 'Epic' },
      { value: 'Rare', label: 'Rare' },
      { value: 'Common', label: 'Common' },
    ],
  },
  {
    key: 'sort',
    label: 'Sort by',
    options: [
      { value: '', label: 'Level: High → Low' },
      { value: 'level-asc', label: 'Level: Low → High' },
      { value: 'rarity-desc', label: 'Rarity: Legendary → Common' },
      { value: 'name-asc', label: 'Name: A → Z' },
    ],
  },
]

const RARITY_ORDER: Record<Rarity, number> = {
  Legendary: 4,
  Epic: 3,
  Rare: 2,
  Common: 1,
}

/* Rarity is game data, so its colours are tokens declared in the app's own
 * @theme — not raw palette classes, and not tokens the package has to carry. */
const RARITY_TEXT: Record<Rarity, string> = {
  Common: 'text-rarity-common',
  Rare: 'text-rarity-rare font-semibold',
  Epic: 'text-rarity-epic font-semibold',
  Legendary: 'text-rarity-legendary font-semibold',
}

export function PlayerCardsGrid({ cards }: { cards: readonly PlayerCard[] }) {
  const [tableState, setTableState] = useState<TableState>({
    search: '',
    filters: {},
    sortKey: '',
    sortDir: 'desc',
    page: 1,
    pageSize: 50,
  })

  const handleStateChange = (patch: Partial<TableState>) => {
    setTableState((prev) => ({ ...prev, ...patch }))
  }

  const filteredCards = useMemo(() => {
    let result = [...cards]
    const searchQ = tableState.search.trim().toLowerCase()
    const rarity = tableState.filters.rarity
    const sort = tableState.filters.sort || 'level-desc'

    if (searchQ) {
      result = result.filter(
        (c) => c.name.toLowerCase().includes(searchQ) || c.rarity.toLowerCase().includes(searchQ),
      )
    }

    if (rarity) {
      result = result.filter((c) => c.rarity === rarity)
    }

    result.sort((a, b) => {
      if (sort === 'level-asc') return a.level - b.level
      if (sort === 'rarity-desc') return RARITY_ORDER[b.rarity] - RARITY_ORDER[a.rarity]
      if (sort === 'name-asc') return a.name.localeCompare(b.name)
      return b.level - a.level
    })

    return result
  }, [cards, tableState])

  return (
    <div className="space-y-4">
      <DataTableToolbar
        state={tableState}
        onStateChange={handleStateChange}
        searchPlaceholder="Search cards by name…"
        filters={CARD_FILTERS}
        actions={
          <Badge variant="outline" className="text-fg-subtle text-xs tabular-nums">
            {filteredCards.length} / {cards.length} Cards
          </Badge>
        }
      />

      {filteredCards.length === 0 ? (
        <Card className="text-fg-subtle p-10 text-center text-sm">
          No cards match your search filter.
        </Card>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {filteredCards.map((card) => (
            <Card
              key={card.name}
              className="group hover:border-border-strong hover:bg-bg-subtle flex min-w-0 flex-col items-center p-2.5 transition-colors duration-100 ease-out"
            >
              {card.image && (
                <div className="my-1 flex h-24 w-full items-center justify-center p-0.5">
                  <img
                    src={card.image}
                    alt=""
                    width={302}
                    height={363}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-auto max-w-full object-contain transition-transform duration-100 ease-out group-hover:scale-105"
                  />
                </div>
              )}

              <p className="text-fg mt-1 mb-0 w-full truncate text-center text-xs font-semibold tracking-tight">
                {card.name}
              </p>

              <p className="mt-0.5 mb-0 w-full truncate text-center text-xs whitespace-nowrap tabular-nums">
                <span className="text-fg-subtle font-medium">Lvl {card.level}</span>
                <span aria-hidden className="text-border-strong mx-1">
                  •
                </span>
                <span className={RARITY_TEXT[card.rarity]}>{card.rarity}</span>
              </p>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

export function PlayerBattlesList({ battles }: { battles: readonly Battle[] }) {
  return (
    <SectionCard title="Recent Match History" description="Last 6 ranked battles played.">
      {battles.map((battle) => (
        <CardListRow key={battle.id} className="gap-4">
          <Badge variant={battle.result === 'Win' ? 'success' : 'danger'}>{battle.result}</Badge>
          <span className="text-fg min-w-0 flex-1 truncate text-sm font-medium">
            vs {battle.opponent}
          </span>
          <span className="text-fg-muted shrink-0 text-sm tabular-nums">
            Crowns: {battle.crowns}
          </span>
          <span className="text-fg-subtle w-20 shrink-0 text-right text-xs tabular-nums">
            {battle.ago}
          </span>
        </CardListRow>
      ))}
    </SectionCard>
  )
}

export function PlayerBanHistoryList({ records }: { records: readonly BanRecord[] }) {
  return (
    <SectionCard
      title="Ban & Enforcement History"
      description="Audit log of account suspensions, bans, warnings, and unban reasons."
    >
      {records.map((rec) => (
        <CardListRow
          key={rec.id}
          className="flex-col items-start gap-2.5 py-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <Badge
                variant={
                  rec.action === 'Banned'
                    ? 'danger'
                    : rec.action === 'Unbanned'
                      ? 'success'
                      : 'warning'
                }
              >
                {rec.action}
              </Badge>
              <span className="text-fg-subtle text-xs font-semibold" translate="no">
                {rec.id}
              </span>
              <span className="text-fg-subtle text-xs font-medium">
                <span aria-hidden>• </span>Action by:{' '}
                <span className="text-fg font-semibold">{rec.operator}</span>
              </span>
            </div>
            <p className="text-fg m-0 text-sm leading-snug font-medium">
              <span className="text-fg-subtle font-semibold">Reason:</span> {rec.reason}
            </p>
            {rec.expiresAt && (
              <p className="text-fg-subtle m-0 text-xs font-medium">
                Duration / Expiry: <span className="text-fg font-medium">{rec.expiresAt}</span>
              </p>
            )}
          </div>
          <span className="text-fg-subtle shrink-0 text-xs tabular-nums">{rec.timestamp}</span>
        </CardListRow>
      ))}
    </SectionCard>
  )
}
